import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Copy,
  Loader2,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  offersKundeLink,
  useCreateProject,
  useCustomHostnameStatus,
  useDhCustomers,
  useDryRun,
  useRegisterCustomHostname,
  type CreatedProject,
  type ProjectWorkspace,
} from "../lib/project-wizard";
import { Combobox } from "../components/Combobox";

const STEPS = [
  { id: 1, label: "Identität" },
  { id: 2, label: "Subdomain" },
  { id: 3, label: "Erlaubte Domains" },
  { id: 4, label: "Custom Hostname" },
  { id: 5, label: "Review" },
] as const;

interface WizardState {
  name: string;
  workspace: ProjectWorkspace;
  kundeRecId: string;
  funnelIdMode: "auto" | "manual";
  manualFunnelId: string;
  subdomainMode: "auto" | "manual";
  manualSubdomain: string;
  allowedDomains: string[];
  enableCustomHostname: boolean;
  customHostname: string;
}

const INITIAL: WizardState = {
  name: "",
  workspace: "agentur",
  kundeRecId: "",
  funnelIdMode: "auto",
  manualFunnelId: "",
  subdomainMode: "auto",
  manualSubdomain: "",
  allowedDomains: [],
  enableCustomHostname: false,
  customHostname: "",
};

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} kopiert`),
    () => toast.error("Kopieren fehlgeschlagen"),
  );
}

export function ProjectWizardPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const stepParam = parseInt(params.get("step") || "1", 10);
  const step = Math.min(Math.max(1, isNaN(stepParam) ? 1 : stepParam), 5);

  const [state, setState] = useState<WizardState>(INITIAL);
  const [created, setCreated] = useState<CreatedProject | null>(null);

  const setStep = (n: number) => {
    const next = new URLSearchParams(params);
    next.set("step", String(n));
    setParams(next, { replace: true });
  };

  const update = <K extends keyof WizardState>(k: K, v: WizardState[K]) =>
    setState((s) => ({ ...s, [k]: v }));

  // Debounced dry-run for steps 1+2
  const dryRunInput = useMemo(
    () => ({
      name: state.name,
      funnelId:
        state.funnelIdMode === "manual" && state.manualFunnelId
          ? state.manualFunnelId
          : undefined,
      subdomain:
        state.subdomainMode === "manual" && state.manualSubdomain
          ? state.manualSubdomain
          : undefined,
    }),
    [state],
  );

  const dry = useDryRun(dryRunInput, !created && state.name.length >= 2);

  const stepValid = useMemo(() => {
    if (created) return true;
    if (step === 1) {
      if (!state.name.trim()) return false;
      if (state.funnelIdMode === "manual" && !state.manualFunnelId) return false;
      if (dry.data && !dry.data.funnelIdAvailable) return false;
      return true;
    }
    if (step === 2) {
      if (state.subdomainMode === "manual" && !state.manualSubdomain) return false;
      if (dry.data && !dry.data.subdomainAvailable) return false;
      if (
        state.subdomainMode === "manual" &&
        dry.data?.issues.some((i) =>
          ["subdomain_format_invalid", "subdomain_reserved"].includes(i),
        )
      )
        return false;
      return true;
    }
    // Allowed domains is optional (issue #160): an empty list means
    // "allow all" (soft-rollout in domain-allowlist.ts), so nothing breaks —
    // enforcement kicks in once the first domain is added later.
    if (step === 3) return true;
    if (step === 4) {
      if (state.enableCustomHostname && !state.customHostname.trim()) return false;
      return true;
    }
    return true;
  }, [step, state, dry.data, created]);

  const create = useCreateProject();

  const submitCreate = () => {
    create.mutate(
      {
        name: state.name.trim(),
        funnelId:
          state.funnelIdMode === "manual" && state.manualFunnelId
            ? Number(state.manualFunnelId)
            : undefined,
        subdomain:
          state.subdomainMode === "manual" && state.manualSubdomain
            ? state.manualSubdomain.trim().toLowerCase()
            : undefined,
        allowedDomains: state.allowedDomains,
        kundeRecId: offersKundeLink(state.workspace)
          ? state.kundeRecId || null
          : null,
        workspace: state.workspace,
      },
      {
        onSuccess: (res) => {
          setCreated(res.project);
          toast.success("Projekt erstellt");
          if (offersKundeLink(state.workspace) && state.kundeRecId) {
            if (res.kundeLink?.ok) {
              toast.success("Kunde verknüpft (dh_portal aktualisiert)");
            } else {
              toast.warning(
                `Kunde nicht verknüpft: ${res.kundeLink?.error ?? "unbekannt"}`,
              );
            }
          }
        },
        onError: (err) =>
          toast.error(`Erstellung fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-wizard space-y-6">
        <header>
          <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Neues Projekt
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {created ? "Bereit." : "Wizard"}
          </h1>
        </header>

        {!created && <Stepper current={step} onJump={setStep} />}

        {created ? (
          <SuccessScreen
            project={created}
            wizardHostname={
              state.enableCustomHostname && state.customHostname.trim()
                ? state.customHostname.trim()
                : undefined
            }
          />
        ) : (
          <div className="rounded-md border border-border-primary bg-bg-secondary p-6">
            {step === 1 && (
              <StepIdentity
                state={state}
                update={update}
                suggestedFunnelId={dry.data?.suggestedFunnelId}
                funnelIdAvailable={dry.data?.funnelIdAvailable ?? true}
                checking={dry.isFetching}
              />
            )}
            {step === 2 && (
              <StepSubdomain
                state={state}
                update={update}
                suggestedSubdomain={dry.data?.suggestedSubdomain ?? ""}
                subdomainAvailable={dry.data?.subdomainAvailable ?? true}
                issues={dry.data?.issues ?? []}
                checking={dry.isFetching}
              />
            )}
            {step === 3 && <StepDomains state={state} update={update} />}
            {step === 4 && <StepCustomHostname state={state} update={update} />}
            {step === 5 && <StepReview state={state} />}
          </div>
        )}

        {!created && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => (step === 1 ? navigate("/projects") : setStep(step - 1))}
              className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
            >
              <ArrowLeft size={14} />
              {step === 1 ? "Abbrechen" : "Zurück"}
            </button>
            {step < 5 ? (
              <button
                type="button"
                onClick={() => stepValid && setStep(step + 1)}
                disabled={!stepValid}
                className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
              >
                Weiter
                <ArrowRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={submitCreate}
                disabled={create.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-accent px-4 py-2 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-60"
              >
                {create.isPending && (
                  <Loader2 size={14} className="animate-spin" />
                )}
                Projekt erstellen
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({
  current,
  onJump,
}: {
  current: number;
  onJump: (n: number) => void;
}) {
  return (
    <ol className="flex items-center gap-2">
      {STEPS.map((s) => {
        const active = s.id === current;
        const done = s.id < current;
        return (
          <li key={s.id} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => done && onJump(s.id)}
              disabled={!done}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full border text-xs font-medium ${
                active
                  ? "border-accent bg-accent text-text-on-accent"
                  : done
                    ? "border-border-primary bg-bg-secondary text-text-primary cursor-pointer hover:bg-bg-hover"
                    : "border-border-primary bg-bg-secondary text-text-tertiary"
              }`}
            >
              {done ? <Check size={12} /> : s.id}
            </button>
            <span
              className={`text-xs font-medium ${active ? "text-text-primary" : "text-text-tertiary"}`}
            >
              {s.label}
            </span>
            {s.id < STEPS.length && (
              <span className="mx-1 h-px w-6 bg-border-primary" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function FieldLabel({
  label,
  hint,
}: {
  label: string;
  hint?: string;
}) {
  return (
    <div className="mb-1.5">
      <label className="block text-sm font-medium text-text-primary">
        {label}
      </label>
      {hint && <p className="text-xs text-text-tertiary">{hint}</p>}
    </div>
  );
}

function StepIdentity({
  state,
  update,
  suggestedFunnelId,
  funnelIdAvailable,
  checking,
}: {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  suggestedFunnelId: number | undefined;
  funnelIdAvailable: boolean;
  checking: boolean;
}) {
  const dhCustomers = useDhCustomers();
  const customers = dhCustomers.data?.customers ?? [];
  const dhConfigured = dhCustomers.data?.configured ?? false;

  return (
    <div className="space-y-5">
      <div>
        <FieldLabel
          label="Projekt-Name"
          hint="Name des Kunden oder Funnels. Wird in der Übersicht und im BQ-Dataset verwendet."
        />
        <input
          type="text"
          autoFocus
          value={state.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="z.B. Coaching Müller"
          className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
        />
      </div>

      <div>
        <FieldLabel
          label="Workspace"
          hint="Agentur = eigene Kundenprojekte. marketingabteilung.ai = vom Partner angelegte Projekte."
        />
        <select
          value={state.workspace}
          onChange={(e) =>
            update("workspace", e.target.value as ProjectWorkspace)
          }
          className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 text-sm text-text-primary focus:border-border-focus focus:outline-none"
        >
          <option value="agentur">Agentur</option>
          <option value="marketingabteilung">marketingabteilung.ai</option>
        </select>
      </div>

      {offersKundeLink(state.workspace) && (
        <div>
          <FieldLabel
            label="DHC-Kunde (optional)"
            hint="Verknüpft das Projekt mit einem DHC-Kunden (schreibt funnel_mark + Projektname nach dh_portal). „Kein fester Kunde“ lassen, wenn es keinem Kunden fest zugeordnet ist."
          />
          {dhConfigured ? (
            <Combobox
              value={state.kundeRecId}
              options={customers.map((k) => ({
                value: k.kundeRecId,
                label: k.company ?? k.kundeRecId,
              }))}
              onChange={(v) => update("kundeRecId", v)}
              placeholder="DHC-Kunde suchen… (leer = kein fester Kunde)"
            />
          ) : (
            <p className="text-xs text-text-tertiary">
              {dhCustomers.isLoading
                ? "Lade Kundenliste…"
                : "Kundenverknüpfung nicht konfiguriert (SUPABASE_URL/SERVICE_ROLE_KEY fehlen) — überspringen."}
            </p>
          )}
        </div>
      )}

      <div>
        <FieldLabel
          label="Funnel-ID"
          hint="ablecdp-Kompatibilität. Auto = nächste freie ID."
        />
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="funnelId"
              checked={state.funnelIdMode === "auto"}
              onChange={() => update("funnelIdMode", "auto")}
            />
            <span className="text-text-primary">
              Auto-zuweisen
              {suggestedFunnelId && (
                <span className="ml-2 font-mono text-xs text-text-tertiary">
                  → {suggestedFunnelId}
                </span>
              )}
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="funnelId"
              checked={state.funnelIdMode === "manual"}
              onChange={() => update("funnelIdMode", "manual")}
            />
            <span className="text-text-primary">Manuell</span>
            <input
              type="number"
              value={state.manualFunnelId}
              onChange={(e) => update("manualFunnelId", e.target.value)}
              onFocus={() => update("funnelIdMode", "manual")}
              placeholder="z.B. 42"
              className="ml-2 h-8 w-32 rounded-md border border-border-primary bg-bg-primary px-2 font-mono text-xs text-text-primary focus:border-border-focus focus:outline-none"
            />
            {state.funnelIdMode === "manual" && state.manualFunnelId && (
              <Availability available={funnelIdAvailable} checking={checking} />
            )}
          </label>
        </div>
      </div>
    </div>
  );
}

function StepSubdomain({
  state,
  update,
  suggestedSubdomain,
  subdomainAvailable,
  issues,
  checking,
}: {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  suggestedSubdomain: string;
  subdomainAvailable: boolean;
  issues: string[];
  checking: boolean;
}) {
  const formatIssue = issues.includes("subdomain_format_invalid");
  const reservedIssue = issues.includes("subdomain_reserved");

  return (
    <div className="space-y-5">
      <div>
        <FieldLabel
          label="Tracking-Subdomain"
          hint="Auf dhc-track.io. Default: kryptisch (8 Zeichen). Kann später nicht mehr geändert werden."
        />
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="subdomain"
              checked={state.subdomainMode === "auto"}
              onChange={() => update("subdomainMode", "auto")}
            />
            <span className="text-text-primary">Auto-generieren</span>
            <span className="ml-2 font-mono text-xs text-text-secondary">
              {suggestedSubdomain
                ? `${suggestedSubdomain}.dhc-track.io`
                : "lade…"}
            </span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="subdomain"
              checked={state.subdomainMode === "manual"}
              onChange={() => update("subdomainMode", "manual")}
            />
            <span className="text-text-primary">Eigene</span>
            <input
              type="text"
              value={state.manualSubdomain}
              onChange={(e) =>
                update(
                  "manualSubdomain",
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                )
              }
              onFocus={() => update("subdomainMode", "manual")}
              placeholder="z.B. coach-mueller"
              className="ml-2 h-8 w-44 rounded-md border border-border-primary bg-bg-primary px-2 font-mono text-xs text-text-primary focus:border-border-focus focus:outline-none"
            />
            <span className="font-mono text-xs text-text-tertiary">
              .dhc-track.io
            </span>
          </label>
          {state.subdomainMode === "manual" && state.manualSubdomain && (
            <div className="ml-6 text-xs">
              {formatIssue && (
                <p className="text-danger">
                  Format ungültig — 3–32 Zeichen, nur a-z, 0-9, Bindestrich, kein
                  Bindestrich an Rand.
                </p>
              )}
              {reservedIssue && (
                <p className="text-danger">Reservierter Name.</p>
              )}
              {!formatIssue && !reservedIssue && (
                <Availability
                  available={subdomainAvailable}
                  checking={checking}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepDomains({
  state,
  update,
}: {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
}) {
  const [input, setInput] = useState("");

  const add = () => {
    // Tolerate a pasted URL/scheme/path — keep only the bare host.
    const d = input
      .trim()
      .toLowerCase()
      .replace(/^[a-z]+:\/\//, "")
      .split("/")[0]!
      .split("?")[0]!
      .split(":")[0]!
      .replace(/^\*\./, "");
    if (!d) return;
    if (state.allowedDomains.includes(d)) {
      toast.error("Domain bereits vorhanden");
      return;
    }
    update("allowedDomains", [...state.allowedDomains, d]);
    setInput("");
  };

  const remove = (d: string) =>
    update(
      "allowedDomains",
      state.allowedDomains.filter((x) => x !== d),
    );

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-warning bg-warning-bg px-3 py-2 text-xs text-text-secondary">
        <strong className="text-text-primary">Empfohlen, aber optional.</strong> Die Liste steuert
        drei Dinge: welche Domains tracken dürfen (CORS), dass Consent-Banner &amp; Tracking{" "}
        <em>nur</em> auf diesen Domains laufen (Schutz vor Einsatz auf fremden Seiten) und das
        Standard-Ziel des wöchentlichen Cookie-Scanners. <strong className="text-text-primary">
        Leer = keine Einschränkung:</strong> Events werden dann von beliebigen Origins akzeptiert
        und der Cookie-Scanner hat kein Ziel. Domains lassen sich jederzeit später im Projekt
        nachtragen — der Schutz greift automatisch ab der ersten Domain. Subdomains sind
        automatisch eingeschlossen — <code>kunde.de</code> deckt auch <code>www.kunde.de</code>,{" "}
        <code>app.kunde.de</code> usw.
      </div>
      <div>
        <FieldLabel
          label="Erlaubte Domains"
          hint="Bare Domain ohne https:// — z.B. kunde.de. Subdomains sind automatisch mit abgedeckt."
        />
        <div className="flex flex-wrap gap-1.5">
          {state.allowedDomains.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-primary px-2 py-1 font-mono text-xs text-text-primary"
            >
              {d}
              <button
                type="button"
                onClick={() => remove(d)}
                aria-label={`${d} entfernen`}
                className="text-text-tertiary hover:text-danger"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                add();
              }
            }}
            placeholder="z.B. landingpage.de"
            className="h-9 flex-1 rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
          />
          <button
            type="button"
            onClick={add}
            className="rounded-md border border-border-primary bg-bg-primary px-3 text-sm font-medium text-text-primary hover:bg-bg-hover"
          >
            Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}

function StepCustomHostname({
  state,
  update,
}: {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
}) {
  return (
    <div className="space-y-5">
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={state.enableCustomHostname}
          onChange={(e) => update("enableCustomHostname", e.target.checked)}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium text-text-primary">
            Custom Hostname (eigene Subdomain des Kunden)
          </span>
          <span className="block text-xs text-text-tertiary">
            Optional. Wenn der Kunde z.B. <code>t.kunde.de</code> nutzen will.
            Cert wird automatisch von Cloudflare ausgestellt.
          </span>
        </span>
      </label>

      {state.enableCustomHostname && (
        <div>
          <FieldLabel
            label="Hostname"
            hint="Der Kunde muss diesen Namen via CNAME auf dhc-track.io zeigen. CF prüft bei Registrierung."
          />
          <p className="mb-2 text-xs text-text-secondary">
            Idealerweise eine Subdomain der Hauptdomain, auf der auch die
            Landingpages laufen (Landingpages auf <code>kunde.de</code> →{" "}
            <code>t.kunde.de</code>). So läuft das Tracking First-Party über
            die eigene Domain des Kunden.
          </p>
          <input
            type="text"
            value={state.customHostname}
            onChange={(e) =>
              update(
                "customHostname",
                e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""),
              )
            }
            placeholder="z.B. t.kunde.de"
            className="h-9 w-full rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
          />
          <p className="mt-2 text-xs text-text-tertiary">
            Hinweis: Der Hostname wird direkt nach der Projekt-Erstellung
            automatisch registriert. Du kannst diesen Schritt auch überspringen
            und später aus dem Projekt-Detail erledigen.
          </p>
        </div>
      )}
    </div>
  );
}

function StepReview({ state }: { state: WizardState }) {
  const dhCustomers = useDhCustomers();
  const kundeName = state.kundeRecId
    ? (dhCustomers.data?.customers.find((k) => k.kundeRecId === state.kundeRecId)
        ?.company ?? state.kundeRecId)
    : "Kein fester Kunde";
  return (
    <dl className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
      <Row label="Name" value={state.name || "—"} />
      <Row
        label="Workspace"
        value={
          state.workspace === "marketingabteilung"
            ? "marketingabteilung.ai"
            : "Agentur"
        }
      />
      {offersKundeLink(state.workspace) && (
        <Row label="DHC-Kunde" value={kundeName} />
      )}
      <Row
        label="Funnel-ID"
        value={
          state.funnelIdMode === "auto"
            ? "Auto"
            : state.manualFunnelId || "—"
        }
        mono
      />
      <Row
        label="Subdomain"
        value={
          state.subdomainMode === "auto"
            ? "Auto-generiert"
            : state.manualSubdomain
              ? `${state.manualSubdomain}.dhc-track.io`
              : "—"
        }
        mono
      />
      <Row
        label="Erlaubte Domains"
        value={
          state.allowedDomains.length === 0
            ? "Keine — Events von beliebigen Origins werden akzeptiert (später nachtragbar)"
            : `${state.allowedDomains.join(", ")} (inkl. Subdomains)`
        }
      />
      <Row
        label="Custom Hostname"
        value={
          state.enableCustomHostname ? state.customHostname || "—" : "Aus"
        }
        mono
      />
    </dl>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="text-text-secondary">{label}</dt>
      <dd className={`text-text-primary ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </dd>
    </>
  );
}

function Availability({
  available,
  checking,
}: {
  available: boolean;
  checking: boolean;
}) {
  if (checking)
    return (
      <span className="inline-flex items-center gap-1 text-xs text-text-tertiary">
        <Loader2 size={11} className="animate-spin" /> Prüfe…
      </span>
    );
  return available ? (
    <span className="inline-flex items-center gap-1 text-xs text-success">
      <Check size={11} /> verfügbar
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-danger">
      <X size={11} /> belegt
    </span>
  );
}

function SuccessScreen({
  project,
  wizardHostname,
}: {
  project: CreatedProject;
  wizardHostname?: string;
}) {
  const customScriptTag = wizardHostname
    ? project.scriptTag.replaceAll(
        `${project.subdomain}.dhc-track.io`,
        wizardHostname,
      )
    : null;

  return (
    <div className="space-y-6 rounded-md border border-accent bg-accent-subtle p-6">
      <div className="flex items-start gap-3">
        <Check
          size={20}
          className="mt-0.5 shrink-0 rounded-full bg-accent p-0.5 text-text-on-accent"
        />
        <div>
          <p className="font-medium text-text-primary">
            Projekt #{project.id} angelegt
          </p>
          <p className="text-sm text-text-secondary">
            Funnel-ID {project.funnelId} · {project.subdomain}.dhc-track.io
          </p>
        </div>
      </div>

      <PostCreateCustomHostname
        projectId={project.id}
        initialHostname={wizardHostname}
      />

      {customScriptTag && (
        <div>
          <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Script-Tag mit Custom Hostname (auf Landingpage einbinden)
          </p>
          <pre className="overflow-auto rounded-md border border-border-primary bg-bg-elevated p-3 font-mono text-xs leading-relaxed text-text-primary">
            {customScriptTag}
          </pre>
          <button
            type="button"
            onClick={() => copyToClipboard(customScriptTag, "Script-Tag")}
            className="mt-2 inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-elevated px-2 py-1 text-xs text-text-primary hover:bg-bg-hover"
          >
            <Copy size={12} /> Kopieren
          </button>
          <p className="mt-2 text-xs text-text-secondary">
            Funktioniert erst, wenn der Custom Hostname aktiv ist (DNS-Records
            gesetzt + SSL-Cert ausgestellt, siehe oben). Bis dahin kannst du
            den Standard-Tag unten nutzen.
          </p>
        </div>
      )}

      <div>
        <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
          {customScriptTag
            ? "Script-Tag mit Standard-Subdomain (funktioniert sofort)"
            : "Script-Tag (auf Landingpage einbinden)"}
        </p>
        <pre className="overflow-auto rounded-md border border-border-primary bg-bg-elevated p-3 font-mono text-xs leading-relaxed text-text-primary">
          {project.scriptTag}
        </pre>
        <button
          type="button"
          onClick={() => copyToClipboard(project.scriptTag, "Script-Tag")}
          className="mt-2 inline-flex items-center gap-1 rounded-md border border-border-primary bg-bg-elevated px-2 py-1 text-xs text-text-primary hover:bg-bg-hover"
        >
          <Copy size={12} /> Kopieren
        </button>
      </div>

      <div>
        <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
          API-Secret (jetzt kopieren — danach nur noch maskiert sichtbar)
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 overflow-auto rounded-md border border-border-primary bg-bg-elevated px-3 py-1.5 font-mono text-xs text-text-primary">
            {project.apiSecret}
          </code>
          <button
            type="button"
            onClick={() => copyToClipboard(project.apiSecret, "Secret")}
            className="rounded-md border border-border-primary bg-bg-elevated px-2 py-1 text-xs hover:bg-bg-hover"
          >
            <Copy size={12} />
          </button>
        </div>
      </div>

      <div className="flex gap-2">
        <Link
          to={`/projects/${project.id}/overview`}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover"
        >
          Zur Detail-Ansicht
        </Link>
        <Link
          to="/projects/new?step=1"
          onClick={() => window.location.reload()}
          className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
        >
          Noch ein Projekt
        </Link>
      </div>
    </div>
  );
}

function PostCreateCustomHostname({
  projectId,
  initialHostname,
}: {
  projectId: number;
  initialHostname?: string;
}) {
  const [hostname, setHostname] = useState(initialHostname ?? "");
  const [registered, setRegistered] = useState(false);
  const status = useCustomHostnameStatus(
    registered ? projectId : null,
    registered ? 5_000 : false,
  );
  const register = useRegisterCustomHostname(projectId);

  // Hostname aus dem Wizard-Step direkt registrieren statt erneut zu fragen.
  const [autoFired, setAutoFired] = useState(false);
  useEffect(() => {
    if (!initialHostname || autoFired) return;
    setAutoFired(true);
    register.mutate(initialHostname, {
      onSuccess: () => {
        setRegistered(true);
        toast.success("Custom Hostname registriert, Validierung läuft");
      },
      onError: (err) =>
        toast.error(
          `Hostname-Registrierung fehlgeschlagen: ${(err as Error).message} — du kannst es unten erneut versuchen.`,
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHostname, autoFired]);

  if (registered && status.data) {
    return (
      <CustomHostnameStatusCard
        status={status.data}
        loading={status.isFetching}
      />
    );
  }

  return (
    <div className="rounded-md border border-border-primary bg-bg-elevated p-4">
      <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
        Custom Hostname (optional, später möglich)
      </p>
      <p className="mt-1 text-xs text-text-secondary">
        Falls der Kunde eine eigene Subdomain wie <code>t.kunde.de</code>{" "}
        nutzen will. CNAME muss auf <code>dhc-track.io</code> zeigen.
      </p>
      <div className="mt-3 flex gap-2">
        <input
          type="text"
          value={hostname}
          onChange={(e) =>
            setHostname(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""))
          }
          placeholder="t.kunde.de"
          className="h-9 flex-1 rounded-md border border-border-primary bg-bg-primary px-3 font-mono text-sm text-text-primary focus:border-border-focus focus:outline-none"
        />
        <button
          type="button"
          disabled={!hostname || register.isPending}
          onClick={() =>
            register.mutate(hostname, {
              onSuccess: () => {
                setRegistered(true);
                toast.success("Hostname registriert, Validierung läuft");
              },
              onError: (err) =>
                toast.error(
                  `Registrierung fehlgeschlagen: ${(err as Error).message}`,
                ),
            })
          }
          className="rounded-md bg-accent px-3 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:opacity-50"
        >
          {register.isPending && (
            <Loader2 size={14} className="mr-1 inline animate-spin" />
          )}
          Registrieren
        </button>
      </div>
    </div>
  );
}

function CustomHostnameStatusCard({
  status,
  loading,
}: {
  status: import("../lib/project-wizard").CustomHostnameStatus;
  loading: boolean;
}) {
  const isActive = status.sslStatus === "active" && status.cfStatus === "active";

  return (
    <div className="rounded-md border border-border-primary bg-bg-elevated p-4">
      <div className="flex items-center gap-2">
        <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
          Custom Hostname
        </p>
        {loading && (
          <RefreshCw size={11} className="animate-spin text-text-tertiary" />
        )}
      </div>
      <p className="mt-1 font-mono text-sm text-text-primary">
        {status.hostname}
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-text-secondary">CF-Status:</span>{" "}
          <code className="text-text-primary">{status.cfStatus ?? "—"}</code>
        </div>
        <div>
          <span className="text-text-secondary">SSL:</span>{" "}
          <code className="text-text-primary">{status.sslStatus ?? "—"}</code>
        </div>
      </div>
      {isActive ? (
        <p className="mt-3 inline-flex items-center gap-1 text-xs text-success">
          <Check size={12} /> Aktiv — Hostname ist live.
        </p>
      ) : (
        <div className="mt-3 space-y-2 text-xs">
          <p className="text-text-secondary">
            Status wird alle 5s aktualisiert. Der Kunde muss noch:
          </p>
          {status.ownershipVerification && (
            <pre className="rounded-md border border-border-primary bg-bg-tertiary p-2 font-mono">
              {status.ownershipVerification.type.toUpperCase()}{" "}
              {status.ownershipVerification.name} ={" "}
              {status.ownershipVerification.value}
            </pre>
          )}
          {status.validationRecords.map((r, i) => (
            <pre
              key={i}
              className="rounded-md border border-border-primary bg-bg-tertiary p-2 font-mono"
            >
              {r.txt_name && `TXT ${r.txt_name} = ${r.txt_value}`}
              {r.http_url && `HTTP ${r.http_url} = ${r.http_body}`}
            </pre>
          ))}
        </div>
      )}
    </div>
  );
}

